import React, { useEffect } from 'react';

import {
  collaboratorsParamKey,
  s4sFlowParamKey,
  templateQuickStartParamKey,
  tourIdKey,
  walkThroughParamKey,
} from '@aline/common';
import { params } from '@aline/routes';
import { AppBar } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useUnmount } from 'react-use';
import { useIntercom } from 'react-use-intercom';
import { $chatThreadState } from 'src/components/ai/chat/chat-state';
import { parseAIChatItem } from 'src/components/ai/chat/ChatPanelMarkdown/helpers/aiChatItem';
import { shareDialogOpenVar } from 'src/editor/AlineEditor';
import {
  useGetStartWalkthrough,
  useNavToNextFieldFromURL,
  useSetRequesterWalkthroughInfo,
} from 'src/editor/toolbars/editor-app-bars/helpers';
import { PublicAppBar } from 'src/editor/toolbars/editor-app-bars/PublicAppBar';
import { useEditorViewPreferences } from 'src/editor/vars';
import { useCurrentUserQuery } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { updateUrlWithoutRefresh, useDocumentParams } from 'src/helpers';
import { useGetCurrentDoc, useOpenDocumentSummaryFromURL } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import type { FileVersionSubset } from 'src/types/apollo/file-version';
import { makeStyles } from 'tss-react/mui';

import { activeAppBarVar, EditorAppBarContent, useActiveAppBar } from './EditorAppBarContent';
import { aiThreadIdVar } from '../right-panel/ai';

const useStyles = makeStyles<StyleProps>()((theme, { walkThroughActive }) => ({
  appBar: {
    backgroundColor: walkThroughActive ? theme.palette.primary.main : theme.palette.common.white,
    zIndex: walkThroughActive ? theme.zIndex.modal + 1 : undefined,
  },
  topToolbarWrapper: {
    alignItems: 'center',
    borderBottom: !walkThroughActive ? `solid 1px ${theme.appColors.rule}` : undefined,
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'nowrap',
    height: theme.appSizes.appBarHeight,
  },
}));

interface StyleProps {
  walkThroughActive: boolean;
}

export interface AppBarDocument extends Pick<
  Apollo.Document,
  'id' | 'name' | 'status' | '__typename' | 'variant' | 'isCommunityTemplate' | 'requesterRole'
> {
  latestVersion: FileVersionSubset;
  workflow: Pick<Apollo.Workflow, 'id'> | null;
}

export const EditorAppBar = React.memo(function EditorAppBar(): JSX.Element | null {
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const { activeAppBar } = useActiveAppBar();
  const { updateActiveTabTitle } = useTabContext();
  const { currentUser, loading: userLoading } = useCurrentUserQuery();

  const { fullScreen } = useEditorViewPreferences();
  const { classes } = useStyles({ walkThroughActive: activeAppBar === 'walkthrough' });

  useEffect(() => {
    if (typeof document?.name === 'string')
      updateActiveTabTitle(document.name || 'Untitled Document');
  }, [document?.name, updateActiveTabTitle]);

  useParseEditorUrlParams();
  useNavToNextFieldFromURL();
  useOpenDocumentSummaryFromURL();
  useSetRequesterWalkthroughInfo();

  // Don't show anything while user is loading to prevent rendering/flicker of PublicAppBar.
  if (userLoading || fullScreen) return null;
  if (!currentUser) return <PublicAppBar />;

  return (
    <>
      <Helmet>
        <title>{document?.name || 'Untitled'}</title>
      </Helmet>
      <AppBar className={classes.appBar} elevation={0} position="relative">
        <div className={classes.topToolbarWrapper}>
          <EditorAppBarContent activeAppBar={activeAppBar} documentId={documentId} />
        </div>
      </AppBar>
    </>
  );
});

export function useParseEditorUrlParams() {
  const { startTour } = useIntercom();
  const { openDrawer } = useGlobalDrawer();
  const { startWalkthrough } = useGetStartWalkthrough();

  useUnmount(() => activeAppBarVar('standard'));

  useEffect(() => {
    const url = new URL(window.location.href);
    const tourIdValue = url.searchParams.get(tourIdKey);
    if (tourIdValue) {
      url.searchParams.delete(tourIdKey);
      updateUrlWithoutRefresh(url.toString());
      setTimeout(() => startTour(parseInt(tourIdValue)), 2500);
    }
  }, [startTour]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const aiChatItemKey = url.searchParams.get(params.aiChatItem);
    const walkThroughKey = url.searchParams.get(walkThroughParamKey);
    const workflowQuickStartKey = url.searchParams.get(s4sFlowParamKey);
    const editCollaboratorsKey = url.searchParams.get(collaboratorsParamKey);
    const templateQuickStartKey = url.searchParams.get(templateQuickStartParamKey);

    if (walkThroughKey) startWalkthrough();
    if (editCollaboratorsKey) shareDialogOpenVar(true);
    if (workflowQuickStartKey) activeAppBarVar('workflow-quickstart');
    if (templateQuickStartKey) activeAppBarVar('template-quickstart');
    if (aiChatItemKey) {
      const aiChatItem = parseAIChatItem(aiChatItemKey);
      if (aiChatItem) {
        aiThreadIdVar(aiChatItem.threadId);
        $chatThreadState.autoScrollAIChatItem = aiChatItem;
      }
      url.searchParams.delete(params.aiChatItem);
      updateUrlWithoutRefresh(url.toString());
      openDrawer();
    }
  }, [openDrawer, startWalkthrough]);
}
